<template>
  <app-modal>
    <div slot="header">
      <div class="d-flex align-items-center justify-content-between">
        <h3>{{ $t('booleanSearch.' + (isTag ? 'upload_tag' : (variant === 'twitter_v3' ? 'upload_v3' : 'upload'))) }}</h3>
        <span class="mt-2 me-2 cursor-pointer" @click="onToggleHistory"
          v-tooltip="$t('booleanSearch.upload_toggle_history')">
          <i class="far fa-history"></i>
        </span>
      </div>
    </div>
    <div slot="body">

      <div v-if="showHistory">
        <load-content :loading="loadingHistory" class="position-relative" :class="{ 'py-5': loadingHistory }">
          <table class="w-100">
            <thead>
              <th>{{ $t('booleanSearch.upload_header.status') }}</th>
              <th>{{ $t('booleanSearch.upload_header.updated_at') }}</th>
              <th>{{ $t('booleanSearch.upload_header.report') }}</th>
            </thead>
            <tbody>
              <tr v-for="(upload, i) in uploads" :key="i">
                <td>{{ $t('booleanSearch.upload_status.' + upload.status) }}</td>
                <td>{{ formatDate(upload.last_updated_at) }}</td>
                <td><a v-if="upload.report_link" :class="{ 'disabled': !upload.report_link }" :href="upload.report_link"
                    v-tooltip="'Download Report'"><i class="far fa-download"></i></a></td>
              </tr>
            </tbody>
          </table>
        </load-content>
      </div>
      <div v-else>
        <p v-html="$t(isTag ? 'booleanSearch.upload_text_tag' : (variant === 'twitter_v3' ? 'booleanSearch.upload_text_v3' : 'booleanSearch.upload_text'))"></p>

        <i18n v-if="status === 'possibly_stale'" path="booleanSearch.upload_stale_message" tag="div"
          class="mt-3 alert alert-warning">
          <a href="#" @click="onCancelUpload">{{ $t('booleanSearch.upload_stale_action') }}</a>
        </i18n>

        <load-content :loading="loading" class="position-relative" :class="{ 'py-5': loading }">
          <form-renderer :fields="['boolUpload']" :fieldsAdditions="fieldsAdditions" :show-submit="false"
            @updateModel="updateModel" />
          <i18n v-if="reportUrl" path="booleanSearch.upload_complete_report_message" tag="div" class="mt-3">
            <a :href="reportUrl">{{ $t('booleanSearch.upload_complete_report_action') }}</a>
          </i18n>
        </load-content>
        <div v-if="lastPollResponse">
          <div class="mb-4 text-center text-sm">
            <div>{{ lastPollResponse.message }}</div>
            <div>{{ $t('booleanSearch.upload_last_updated_at') }} {{ formatTime(lastPollResponse.updated_at) }}</div>
          </div>
          <div class="alert alert-secondary m-0">{{ $t('booleanSearch.upload_running') }}</div>
        </div>
      </div>
    </div>
    <div slot="footer">
      <div class="btn-container">
        <button v-if="!showHistory" class="btn button-save btn-sm mr10" v-on:click="submit"
          :disabled="buttonsDisabled || fileIsEmpty">
          {{ $t("form.button.upload") }}
        </button>
        <button class="btn btn-default btn-sm" tabindex="1" v-on:click="$emit('close')">
          {{ $t("form.button.close") }}
        </button>
      </div>
    </div>
  </app-modal>
</template>

<script>
// Modules
import { mapState, mapMutations } from 'vuex';
import Axios from 'axios';
import Moment from 'moment';

// Components
import Modal from '@/components/modal';
import FormRenderer from '@/components/form-renderer';
import LoadContent from '@/components/load-content'

export default {
  props: {
    isTag: {
      type: Boolean,
      default: false
    },
    variant: {
      type: String,
      default: 'netfeedr'
    }
  },

  data () {
    return {
      model: {
        file: null
      },

      // pollingUid: null,
      pollingEnabled: false,
      uploading: false,
      pollTimeout: null,
      lastPollResponse: null,
      reportUrl: null,
      status: null,

      uploads: null,
      showHistory: false
    }
  },

  mounted () {
    this.pollingEnabled = true;
    this.getLatestUploads();
  },

  computed: {
    ...mapState({
      pollingUid: state => state.booleanSearch.searches.pollingUid
    }),

    loadingHistory () {
      return !Array.isArray(this.uploads);
    },

    loading () {
      return this.uploading || this.loadingHistory;
    },

    buttonsDisabled () {
      return this.uploading;
    },

    fieldsAdditions () {
      return {
        boolUpload: {
          class: 'd-block m-0'
        }
      }
    },

    fileIsEmpty () {
      const entries = Array.from(this.model?.file?.entries() || []).filter(([key, value]) => value instanceof File);
      return entries.length === 0;
    },

    projectId () {
      return this.$route.params.project_id;
    },

    hasHistory () {
      return this.uploads.length > 0;
    }
  },

  methods: {
    ...mapMutations({
      setPollingUid: 'booleanSearch/SET_POLLING_UID'
    }),

    updateModel(newValue, field) {
      if (!field) return;
      this.model[field.name] = newValue[field.name];
    },

    onToggleHistory () {
      this.showHistory = !this.showHistory;
    },

    formatDate (date) {
      return Moment.utc(date).format('D MMM YYYY HH:mm:ss [(UTC)]');
    },

    formatTime (date) {
      return Moment.utc(date).format('HH:mm:ss [(UTC)]');
    },

    checkStatus (uid) {
      this.uploading = true;
      const endpoint = this.isTag 
        ? `/projects/${this.projectId}/searches/tag_library/import/status/${uid}`
        : `/projects/${this.projectId}/searches/import/status/${uid}`;
      
      return Axios.get(endpoint).then(response => {
        const { status, current_step_description, last_updated_at, report_link, stale } = response.data?.data;
        this.status = status;
        this.lastPollResponse = { message: current_step_description, updated_at: last_updated_at }
        if (status === 'completed') {
          this.stopPolling();
          if (report_link) {
            // It it completed with errors
            this.reportUrl = report_link;
            this.$notify({
              group: 'store_notifications',
              type: 'warning',
              text: this.$t('booleanSearch.upload_complete_with_errors')
            });
          } else {
            // Handle successful completion
            this.$notify({
              group: 'store_notifications',
              type: 'success',
              text: this.$t('booleanSearch.upload_success_message')
            });
            this.$emit('close');
          }
        } else if (status === 'error') {
          // Upload crashed
          this.stopPolling();
          this.$notify({
            group: 'store_notifications',
            type: 'error',
            text: this.$t('booleanSearch.upload_error_message')
          });
        } else {
          // If still in progress, continue polling
          if (this.pollingEnabled) {
            this.pollTimeout = setTimeout(() => {
              this.checkStatus(uid);
            }, 1000);
          }
        }
      });
    },

    submit () {
      this.uploading = true;
      const endpoint = this.isTag 
        ? `/projects/${this.projectId}/searches/tag_library/import`
        : `/projects/${this.projectId}/searches/import`;
        
      Axios.post(endpoint, this.model.file, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      }).then(response => {
        const { uid } = response.data?.data;
        this.setPollingUid(uid);
        this.checkStatus(uid);
      }).catch(error => {
        console.error('Error uploading file:', error);
        this.$notify({
          group: 'store_notifications',
          type: 'error',
          text: this.$t('booleanSearch.upload_error_message')
        });
        this.stopPolling();
      });
    },

    getLatestUploads () {
      this.uploads = null;
      const endpoint = this.isTag 
        ? `/projects/${this.projectId}/searches/tag_library/import`
        : `/projects/${this.projectId}/searches/import`;
        
      return Axios.get(endpoint).then(r => {
        this.uploads = r.data?.data?.jobs || [];
      });
    },

    onCancelUpload() {
      this.uploading = false;
      this.status = null;
      this.stopPolling();
    },

    stopPolling() {
      this.uploading = false;
      this.lastPollResponse = null;
      if (this.pollTimeout) {
        clearTimeout(this.pollTimeout);
        this.pollTimeout = null;
      }
    },
  },

  watch: {
    showHistory (value) {
      if (value) {
        this.getLatestUploads();
      }
    },

    uploads (value) {
      if (value && this.pollingUid) {
        const last = value.find(dt => dt.uid === this.pollingUid);
        if (last.status === 'in_progress') {
          this.checkStatus(last.uid);
        } else {
          this.setPollingUid(null);
        }
      }
    }
  },

  beforeDestroy() {
    this.stopPolling();
    this.pollingEnabled = false;
  },

  components: {
    'app-modal': Modal,
    FormRenderer,
    LoadContent
  },

  name: 'UploadResourcesModal'
}
</script>